import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAccount } from 'wagmi';
import { ILeaderboard } from '@kattana-io/common';
import Text from '../../../../atoms/Text';
import LeaderboardTableRows from './Rows';
import ListExtender from '../../../../molecules/ListExtender';
import { formatAddress } from '../../../../helpers/formatters';
import { toFixed4 } from '../../../../components/DexTrades/formatters';
import { actionSetCompetitionWallet } from '../../../../actions/competition';
import { IStore } from '../../../../reducers';

const HEADERS = [
  '#',
  'Nickname',
  'Fav. Pair',
  '24hVolume',
  'Points',
  'Potential reward',
];

export type LeaderboardDecoratedRow = Record<
  string,
  { formatted: number | string; raw: number | string }
>;

export const decorateLeaderboardTableData = (
  tableData: ILeaderboard[]
): LeaderboardDecoratedRow[] =>
  tableData.map((item, index) => ({
    '#': { formatted: index + 1, raw: index },
    Nickname: {
      formatted: item.nickname || formatAddress(item.wallet),
      raw: item.wallet,
    },
    'Fav. Pair': { formatted: item.pair || '-', raw: item.pair || '' },
    '24hVolume': {
      formatted: toFixed4(item.volume || 0),
      raw: item.volume || 0,
    },
    Points: { formatted: toFixed4(item.points || 0), raw: item.points || 0 },
    'Potential reward': {
      formatted: `${toFixed4(item.reward || 0)} USDT`,
      raw: item.reward || 0,
    },
  }));

const getBreakInfo = (data: any[]) => {
  if (data.length <= 12) {
    return {
      first: {
        slice: 12,
      },
      second: {
        sliceFrom: 0,
        sliceTo: 0,
      },
    };
  }

  if (data.length <= 24) {
    return {
      first: {
        slice: 11,
        extend: {
          max: data.length,
          min: 12,
          compareTo: 11,
        },
      },
      second: {
        sliceFrom: 0,
        sliceTo: 0,
      },
    };
  }

  const secondBreakPoint = Math.floor(data.length / 2) - 5;
  return {
    first: {
      slice: 6,
      extend: {
        max: secondBreakPoint,
        min: 7,
        compareTo: 6,
      },
    },
    second: {
      sliceFrom: secondBreakPoint,
      sliceTo: secondBreakPoint + 4,
      extend: {
        max: data.length,
        min: secondBreakPoint + 5,
        compareTo: 4,
      },
    },
  };
};

interface ILeaderboardTableProps {
  search: string;
  tableData: ILeaderboard[];
}

const LeaderboardTable = ({ search, tableData }: ILeaderboardTableProps) => {
  const active = useSelector((state: IStore) => state.competition.wallet);

  const headerRef = useRef<HTMLDivElement>(null);
  const [data, setData] = useState(decorateLeaderboardTableData(tableData));
  const { first, second } = getBreakInfo(data);
  const [firstDataBreak, setFirstDataBreak] = useState(
    data.slice(0, first.slice)
  );
  const [secondDataBreak, setSecondDataBreak] = useState(
    data.slice(second.sliceFrom, second.sliceTo)
  );

  const { address } = useAccount();
  const dispatch = useDispatch();

  const activeUser = active || address;

  useEffect(() => {
    const updated = decorateLeaderboardTableData(tableData).filter(
      (item) =>
        item['Nickname'].raw
          .toString()
          .toLocaleLowerCase()
          .includes(search.toLocaleLowerCase()) ||
        item['Fav. Pair'].raw
          .toString()
          .toLocaleLowerCase()
          .includes(search.toLocaleLowerCase())
    );

    setData(updated);

    const updatedBreakDataInfo = getBreakInfo(updated);

    setFirstDataBreak(updated.slice(0, updatedBreakDataInfo.first.slice));
    setSecondDataBreak(
      updated.slice(
        updatedBreakDataInfo.second.sliceFrom,
        updatedBreakDataInfo.second.sliceTo
      )
    );
  }, [search, tableData]);

  const toggleFirstDataBreak = () => {
    if (firstDataBreak.length === first.extend?.compareTo) {
      return setFirstDataBreak(data.slice(0, first.extend?.max));
    }
    setFirstDataBreak(data.slice(0, first.slice));
  };

  const toggleSecondDataBreak = () => {
    if (secondDataBreak.length === second.extend?.compareTo) {
      return setSecondDataBreak(data.slice(second.sliceFrom));
    }
    setSecondDataBreak(data.slice(second.sliceFrom, second.sliceTo));
  };

  const handleRowClick = (index: number) => {
    const row = tableData[index];
    if (row.wallet === activeUser) {
      return;
    }
    if (row.wallet === address) {
      dispatch(actionSetCompetitionWallet(undefined));
    } else {
      dispatch(actionSetCompetitionWallet(row.wallet));
    }
  };

  return (
    <div className='leaderboard_table'>
      <div className='leaderboard_table__thead' ref={headerRef}>
        {HEADERS.map((header) => (
          <div key={header} className='leaderboard_table__thead__th'>
            <Text size={12}>{header}</Text>
          </div>
        ))}
      </div>
      <div
        className='leaderboard_table__body'
        style={{ width: headerRef.current?.getBoundingClientRect().width }}
      >
        <LeaderboardTableRows
          rows={firstDataBreak}
          onRowClick={handleRowClick}
          activeUser={activeUser}
        />
        {first.extend && (
          <div className='leaderboard_table__body__tr leaderboard_table__body__tr--break'>
            <ListExtender
              currentNum={
                firstDataBreak.length === first.extend.compareTo
                  ? first.extend.min
                  : first.extend.max
              }
              totalNum={
                firstDataBreak.length === first.extend.compareTo
                  ? first.extend.max
                  : first.extend.min
              }
              gap={16}
              onExtend={toggleFirstDataBreak}
              size={10}
            />
          </div>
        )}
        <LeaderboardTableRows
          rows={secondDataBreak}
          onRowClick={handleRowClick}
          activeUser={activeUser}
        />
        {second.extend && (
          <div className='leaderboard_table__body__tr leaderboard_table__body__tr--break'>
            <ListExtender
              currentNum={
                secondDataBreak.length === second.extend.compareTo
                  ? second.extend.min
                  : second.extend.max
              }
              totalNum={
                secondDataBreak.length === second.extend.compareTo
                  ? second.extend.max
                  : second.extend.min
              }
              gap={16}
              onExtend={toggleSecondDataBreak}
              size={10}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default LeaderboardTable;
